import Typography from '@mui/material/Typography';
import {Box, LinearProgress, Paper} from '@mui/material';
import byteSize from 'byte-size';

import {thumbSx} from '../Media/Asset/AssetThumb.tsx';
import AssetFileIcon from '../Media/Asset/AssetFileIcon.tsx';
import {ReactNode} from 'react';
import assetClasses from '../AssetList/classes.ts';
import {FileBlobThumb} from './FileBlobThumb.tsx';
import {getMIMETypeFromFile} from '@alchemy/core';

type Props = {
    file: File;
    actions?: ReactNode;
    size?: number;
    progress?: number;
    error?: string;
};

export type {Props as FileCardProps};

export default function FileCard({
    file,
    progress,
    error,
    actions,
    size = 100,
}: Props) {
    const mimeType = getMIMETypeFromFile(file) ?? 'unknown';

    return (
        <Paper
            sx={theme => ({
                margin: 'auto',
                ...thumbSx(size, theme),
            })}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                <div className={assetClasses.thumbWrapper}>
                    {[
                        'image/jpeg',
                        'image/png',
                        'image/bmp',
                        'image/gif',
                        'image/webp',
                        'image/svg+xml',
                    ].includes(mimeType) ? (
                        <FileBlobThumb file={file} size={size} />
                    ) : (
                        <div
                            style={{
                                width: size,
                                height: size,
                                objectFit: 'contain',
                            }}
                        >
                            <AssetFileIcon mimeType={mimeType} />
                        </div>
                    )}
                </div>
                <Box
                    sx={{
                        p: 2,
                        flexGrow: 1,
                    }}
                >
                    <Typography
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: '2',
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2,
                            wordBreak: 'break-all',
                        }}
                        gutterBottom
                        variant="subtitle1"
                    >
                        {file.name}
                    </Typography>
                    {error ? (
                        <Typography color="error" variant="body2" gutterBottom>
                            {error}
                        </Typography>
                    ) : null}
                    <Typography variant="body2" gutterBottom>
                        {byteSize(file.size).toString()} • {mimeType}
                    </Typography>
                    {actions}
                </Box>
            </Box>
            {progress !== undefined && progress < 1 ? (
                <LinearProgress variant="determinate" value={progress * 100} />
            ) : null}
        </Paper>
    );
}
